import React, { FC } from "react";
import { defineMessages, useIntl } from "react-intl";
import { InfoCard, InfoCardField } from "@app/components";
import { Button, Form } from "@natera/form";
import { convertPhoneNumberToDisplayValue } from "@app/utils/convertPhoneNumber";
import "./setupProfileReviewInfo.scss";
import { SetupProfileData } from "@app/provider/profile/types";

export const messages = defineMessages({
  setupProfileReviewInfoFirstName: {
    id: "setupProfileReviewInfoFirstName",
    defaultMessage: "First Name",
  },
  setupProfileReviewInfoMiddleName: {
    id: "setupProfileReviewInfoMiddleName",
    defaultMessage: "Middle Name (Optional)",
  },
  setupProfileReviewInfoLastName: {
    id: "setupProfileReviewInfoLastName",
    defaultMessage: "Last Name",
  },
  setupProfileReviewInfoAlternateLastName: {
    id: "setupProfileReviewInfoAlternateLastName",
    defaultMessage: "Alternate Last Name (Optional)",
  },
  setupProfileReviewInfoDateOfBirth: {
    id: "setupProfileReviewInfoDateOfBirth",
    defaultMessage: "Date of Birth",
  },
  setupProfileReviewInfoMobileNumber: {
    id: "setupProfileReviewInfoMobileNumber",
    defaultMessage: "Mobile Number",
  },
  setupProfileReviewInfoZipCode: {
    id: "setupProfileReviewInfoZipCode",
    defaultMessage: "Zip Code",
  },
  setupProfileReviewInfoAddress: {
    id: "setupProfileReviewInfoAddress",
    defaultMessage: "Address",
  },
  setupProfileReviewInfoNotApplicable: {
    id: "setupProfileReviewInfoNotApplicable",
    defaultMessage: "Not Applicable",
  },
  setupProfileReviewInfoEditBtn: {
    id: "setupProfileReviewInfoEditBtn",
    defaultMessage: "Edit",
  },
  setupProfileReviewInfoConfirmBtn: {
    id: "setupProfileReviewInfoConfirmBtn",
    defaultMessage: "Confirm",
  },
});

interface SetupProfileReviewInfoProps {
  profileData: SetupProfileData;
  onEdit: () => void;
  onSubmit: () => void;
  isLoading?: boolean;
}

const SetupProfileReviewInfo: FC<SetupProfileReviewInfoProps> = ({
  profileData,
  onEdit,
  onSubmit,
  isLoading,
}) => {
  const intl = useIntl();

  const displayDateOfBirth = () => {
    const dOB = profileData.dateOfBirth;
    return dOB
      ? new Date(dOB).toLocaleDateString("en-US", {
          timeZone: "UTC",
          month: "long",
          day: "numeric",
          year: "numeric",
        })
      : "";
  };

  const displayAddress = () => {
    if (!profileData.addresses?.length) return "";

    const address = profileData.addresses?.[0];
    const addressParts = [];

    if (address.street1) {
      addressParts.push(address.street1);
    }

    if (address.street2) {
      addressParts.push(address.street2);
    }

    const cityStateZip = [];
    if (address.city) {
      cityStateZip.push(address.city);
    }
    if (address.state) {
      cityStateZip.push(address.state);
    }
    if (address.zipCode) {
      cityStateZip.push(address.zipCode);
    }

    if (cityStateZip.length > 0) {
      addressParts.push(cityStateZip.join(", "));
    }

    return addressParts.join(", ");
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    onSubmit();
  };

  return (
    <Form className="setup-profile-review-info" onSubmit={handleSubmit}>
      <InfoCard>
        <InfoCardField
          label={intl.formatMessage(messages.setupProfileReviewInfoFirstName)}
          value={profileData.firstName}
        />
        <InfoCardField
          label={intl.formatMessage(messages.setupProfileReviewInfoMiddleName)}
          value={
            profileData.middleName ||
            intl.formatMessage(messages.setupProfileReviewInfoNotApplicable)
          }
        />
        <InfoCardField
          label={intl.formatMessage(messages.setupProfileReviewInfoLastName)}
          value={profileData.lastName}
        />
        <InfoCardField
          label={intl.formatMessage(
            messages.setupProfileReviewInfoAlternateLastName
          )}
          value={
            profileData.alternativeLastName ||
            intl.formatMessage(messages.setupProfileReviewInfoNotApplicable)
          }
        />
        <InfoCardField
          label={intl.formatMessage(messages.setupProfileReviewInfoDateOfBirth)}
          value={displayDateOfBirth()}
        />
        <InfoCardField
          label={intl.formatMessage(messages.setupProfileReviewInfoAddress)}
          value={displayAddress()}
        />
        <InfoCardField
          label={intl.formatMessage(
            messages.setupProfileReviewInfoMobileNumber
          )}
          value={convertPhoneNumberToDisplayValue(profileData.phone)}
        />
      </InfoCard>
      <Button type="submit" unelevated loading={isLoading}>
        {!isLoading &&
          intl.formatMessage(messages.setupProfileReviewInfoConfirmBtn)}
      </Button>
      <Button onClick={onEdit} outlined disabled={isLoading}>
        {intl.formatMessage(messages.setupProfileReviewInfoEditBtn)}
      </Button>
    </Form>
  );
};

export default SetupProfileReviewInfo;
